import {
  IDEMPOTENCY_KEY_HEADER,
  governancePolicyListSchema,
  policyVersionSchema,
  votingRoundListSchema,
  votingRoundSchema,
  type ApprovePolicyVersionRequest,
  type CancelRoundRequest,
  type CastVoteRequest,
  type ClosePolicyVersionRequest,
  type CreateRoundRequest,
  type ExtendRoundRequest,
  type ProposePolicyVersionRequest,
  type SetCategoryPolicyRequest,
  type VersionedRequest,
} from '@mfos/shared';
import { useQuery } from '@tanstack/react-query';
import { apiGet, apiPost, apiPostWithHeaders, apiPut } from '../../lib/api-client';
import { topicKey } from '../topics/topics.api';
import { workspaceKey, workspacePath } from '../workspace/workspace.api';
import { useWorkspaceMutation } from '../workspace/workspace-mutation';

export const roundsKey = (workspaceId: string, topicId: string) =>
  [...topicKey(workspaceId, topicId), 'rounds'] as const;
export const policiesKey = (workspaceId: string) =>
  [...workspaceKey(workspaceId), 'governance', 'policies'] as const;

export function useRounds(workspaceId: string, topicId: string) {
  return useQuery({
    queryKey: roundsKey(workspaceId, topicId),
    queryFn: ({ signal }) =>
      apiGet(workspacePath(workspaceId, 'topics', topicId, 'rounds'), {
        schema: votingRoundListSchema,
        signal,
      }),
  });
}

export function useCreateRound(workspaceId: string, topicId: string) {
  return useWorkspaceMutation(workspaceId, (body: CreateRoundRequest) =>
    apiPost(workspacePath(workspaceId, 'topics', topicId, 'rounds'), body, votingRoundSchema),
  );
}

export type RoundAction = 'open' | 'extend' | 'cancel' | 'finalize';
export type RoundActionBody = VersionedRequest | ExtendRoundRequest | CancelRoundRequest;

export function useRoundAction(workspaceId: string) {
  return useWorkspaceMutation(
    workspaceId,
    ({ roundId, action, body }: { roundId: string; action: RoundAction; body: RoundActionBody }) =>
      apiPost(workspacePath(workspaceId, 'rounds', roundId, action), body, votingRoundSchema),
  );
}

/**
 * Casts (or changes) a vote. The idempotency key identifies one user submission: a retry of the
 * same submission must reuse it so the server records the vote at most once.
 */
export function useCastVote(workspaceId: string) {
  return useWorkspaceMutation(
    workspaceId,
    ({
      roundId,
      body,
      idempotencyKey,
    }: {
      roundId: string;
      body: CastVoteRequest;
      idempotencyKey: string;
    }) =>
      apiPostWithHeaders(
        workspacePath(workspaceId, 'rounds', roundId, 'votes'),
        body,
        votingRoundSchema,
        {
          [IDEMPOTENCY_KEY_HEADER]: idempotencyKey,
        },
      ),
  );
}

// --- Governance policies -------------------------------------------------------------------------

export function usePolicies(workspaceId: string, enabled = true) {
  return useQuery({
    queryKey: policiesKey(workspaceId),
    queryFn: ({ signal }) =>
      apiGet(workspacePath(workspaceId, 'governance', 'policies'), {
        schema: governancePolicyListSchema,
        signal,
      }),
    enabled,
  });
}

export function useProposePolicyVersion(workspaceId: string) {
  return useWorkspaceMutation(
    workspaceId,
    ({ policyId, body }: { policyId: string; body: ProposePolicyVersionRequest }) =>
      apiPost(
        workspacePath(workspaceId, 'governance', 'policies', policyId, 'versions'),
        body,
        policyVersionSchema,
      ),
  );
}

export function useApprovePolicyVersion(workspaceId: string) {
  return useWorkspaceMutation(
    workspaceId,
    ({ versionId, body }: { versionId: string; body: ApprovePolicyVersionRequest }) =>
      apiPost(
        workspacePath(workspaceId, 'governance', 'policy-versions', versionId, 'approve'),
        body,
        policyVersionSchema,
      ),
  );
}

export function useClosePolicyVersion(workspaceId: string) {
  return useWorkspaceMutation(
    workspaceId,
    ({
      versionId,
      action,
      body,
    }: {
      versionId: string;
      action: 'reject' | 'withdraw';
      body: ClosePolicyVersionRequest;
    }) =>
      apiPost(
        workspacePath(workspaceId, 'governance', 'policy-versions', versionId, action),
        body,
        policyVersionSchema,
      ),
  );
}

export function useSetCategoryPolicy(workspaceId: string) {
  return useWorkspaceMutation(workspaceId, (body: SetCategoryPolicyRequest) =>
    apiPut(
      workspacePath(workspaceId, 'governance', 'category-rules'),
      body,
      governancePolicyListSchema,
    ),
  );
}
